import { useState } from "react"
import { toast } from "react-toastify"
import { Socket } from "socket.io-client"
import env from "@/env"
import { SocketCreate, SocketEmit } from "@/utils/socketio.utils"
import { ASSISTANT_INSTANT, ASSISTANT_JUDGMENT, ASSISTANT_VISION, cleanJson } from "@/utils/assistant.utils"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { getLowestPreventionValue, mapNamedAttributesToIds } from "@/entities/physical-risk/model/physical-risk.utils"
import { getRiskAttributeNames, normalizeRiskAttributes } from "@/redux/account.types"
import { PhysicalRisk } from "../model/physical-risk.types"
import { RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import { Site } from "@/entities/site/model/site.types"
import { Population } from "@/entities/population/model/population.types"
import type { PhysicalRiskAddContext } from "../model/physical-risk-add-context.types"

type DescriptionStartMode = "CHOICE" | "DESCRIBE"

const DEFAULT_DESCRIPTION_PLACEHOLDER = "Ex : Chute de plain-pied dans le couloir de stockage..."

interface Options {
  applyContextToDraft: () => void
  context: PhysicalRiskAddContext
  createDraftWithContext: (overrides?: Partial<PhysicalRisk>) => PhysicalRisk
  description: string
  dispatch: any
  getContextFamilyId: () => string | null
  linkedPopulations: Population[]
  linkedSites: Site[]
  openEditStep: (risk: PhysicalRisk) => void
  accountRisksSettings: any
  riskFamilies: RiskFamily[]
  setStep: (step: "SELECT") => void
}

export function usePhysicalRiskAssistant({
  applyContextToDraft,
  context,
  createDraftWithContext,
  description,
  dispatch,
  getContextFamilyId,
  linkedPopulations,
  linkedSites,
  openEditStep,
  accountRisksSettings,
  riskFamilies,
  setStep
}: Options) {
  const [assistantLoadingText, setAssistantLoadingText] = useState("Je suis en train de préparer la fiche du risque.")
  const [descriptionPlaceholder, setDescriptionPlaceholder] = useState(DEFAULT_DESCRIPTION_PLACEHOLDER)
  const [descriptionStartMode, setDescriptionStartMode] = useState<DescriptionStartMode>("CHOICE")
  const [isAssistantLoading, setIsAssistantLoading] = useState(false)

  function startAssistant(text = "Je suis en train de préparer la fiche du risque.") {
    setAssistantLoadingText(text)
    setIsAssistantLoading(true)
  }

  function stopAssistant() {
    setIsAssistantLoading(false)
  }

  function resetToSelect() {
    stopAssistant()
    setStep("SELECT")
  }

  //Les prompts parlent en libelles d'attributs, les reponses sont donc indexees par libelle :
  //mapNamedAttributesToIds les replace sur les ids de stockage avant de remplir le brouillon
  const attributeNames = getRiskAttributeNames(accountRisksSettings.riskAttributes)

  function handleUploadImage(photoId: string) {
    startAssistant()
    dispatch(physicalRiskStore.actions.setActive(null))
    applyContextToDraft()

    const socket: Socket = SocketCreate()
    new Promise((resolve, reject) => {
      SocketEmit(
        socket,
        "task-custom",
        {
          ...ASSISTANT_VISION,
          imageUrl: env.REACT_APP_URL_SPACE.concat("/Photos/", photoId, ".png"),
          preprompt: `Retourner le format JSON suivant { "risk" : {  }, "severity" : 1, "family" : "" }`,
          prompt: `à partir de l'image accessible dans ce lien

          Peux tu m'analyser l'image puis tu me sortir un objet "risk" avec les attributs suivants : ${attributeNames.join(", ")}

          La valeur pour "${attributeNames[0]}" doit décrire l'objet sur l'image en 1 ou 2 mots max.
          
          Peux tu également donner une note pour la gravité (objet "severity" en réponse) à partir de l'échelle suivante (retourner value)

          ${JSON.stringify(accountRisksSettings.severityScale)}
                    
          Enfin - si c'est possible - peux-tu me retourner la famille à partir du tableau suivant dans un objet "family" (${JSON.stringify(riskFamilies)}")`,
          sendMode: "all"
        },
        "fr",
        undefined,
        (res) => {
          try {
            const response = JSON.parse(res)
            if (typeof response.risk !== "object") {
              toast("Impossible d'analyser l'image", { type: "error" })
              resetToSelect()
              return resolve(res)
            }

            //Comme pour la description : la famille reconnue sur l'image prime sur celle du contexte
            const generatedFamilyId = riskFamilies.some((riskFamily) => riskFamily.id === response.family?.id)
              ? response.family.id
              : getContextFamilyId()

            openEditStep(
              createDraftWithContext({
                photoId,
                attributes: mapNamedAttributesToIds(response.risk, accountRisksSettings.riskAttributes),
                RiskFamilyId: generatedFamilyId,
                ...(typeof response.severity === "number" ? { severity: response.severity } : {})
              })
            )
            stopAssistant()
            resolve(res)
          } catch (e) {
            resetToSelect()
            reject(e)
          }
        },
        (err) => {
          resetToSelect()
          reject(err)
        },
        () => {}
      )
    })
  }

  function handleGenerateFromDescription() {
    const trimmedDescription = description.trim()
    if (!trimmedDescription) return

    startAssistant()
    dispatch(physicalRiskStore.actions.setActive(null))
    applyContextToDraft()

    SocketEmit(
      SocketCreate(),
      "task-custom",
      {
        ...ASSISTANT_JUDGMENT,
        preprompt: `Retourner uniquement un JSON valide au format ${JSON.stringify({
          shortName: "intitulé court du risque",
          attributes: Object.fromEntries(attributeNames.map((name) => [name, "valeur non vide"])),
          attributeValues: attributeNames.map(() => "valeur non vide"),
          severity: 1,
          frequency: 1,
          familyId: null
        })}.`,
        prompt: `Tu es un expert en prévention et en évaluation des risques professionnels.

À partir de la description ci-dessous, pré-remplis une fiche de risque en français.

Description fournie par l'utilisateur :
${trimmedDescription}

Contexte :
- Lieux sélectionnés : ${linkedSites.length ? linkedSites.map((site) => site.name).join(", ") : "aucun"}
- Populations sélectionnées : ${
          linkedPopulations.length ? linkedPopulations.map((population) => population.name).join(", ") : "aucune"
        }
- Famille suggérée par le contexte : ${context.type === "family" ? context.family.name : "aucune"}
- Attributs attendus : ${attributeNames.join(", ")}

Règles :
- "attributes" doit contenir exactement toutes les clés attendues, sans en omettre aucune.
- Chaque attribut doit contenir une valeur utile et non vide.
- "attributeValues" doit contenir les mêmes valeurs, dans le même ordre que la liste des attributs attendus.
- "shortName" doit reformuler la description utilisateur en intitulé court et professionnel, 2 à 6 mots maximum, sans copier le texte tel quel.
- Le premier attribut doit reprendre "shortName".
- Les autres attributs doivent compléter concrètement la fiche en exploitant la description et le contexte.
- Choisir la famille la plus pertinente dans cette liste et retourner son id dans "familyId" : ${JSON.stringify(
          riskFamilies.map(({ id, name }) => ({ id, name }))
        )}
- Conserver la famille suggérée par le contexte si elle correspond à la description, sinon retourner celle qui correspond vraiment.
- Proposer une évaluation avec une valeur existante dans chacun de ces barèmes :
  - Gravité : ${JSON.stringify(accountRisksSettings.severityScale)}
  - Fréquence : ${JSON.stringify(accountRisksSettings.frequencyScale)}
- Ne retourner aucun texte en dehors du JSON.`,
        sendMode: "all"
      },
      "fr",
      undefined,
      (res) => {
        try {
          const response = JSON.parse(cleanJson(res))
          const shortName = typeof response.shortName === "string" ? response.shortName.trim() : ""
          const attributeValues = Array.isArray(response.attributeValues) ? response.attributeValues : []
          const generatedAttributes = mapNamedAttributesToIds(response.attributes, accountRisksSettings.riskAttributes)
          const settingsAttributes = normalizeRiskAttributes(accountRisksSettings.riskAttributes)
          const attributes = settingsAttributes.reduce<Record<string, string>>((acc, attribute, index) => {
            const generatedValue = generatedAttributes[attribute.id] || attributeValues[index]
            acc[attribute.id] =
              index === 0 && shortName
                ? shortName
                : typeof generatedValue === "string" && generatedValue.trim()
                  ? generatedValue.trim()
                  : index === 0
                    ? trimmedDescription
                    : ""
            return acc
          }, {})

          const missingAttributes = settingsAttributes.filter((attribute) => !attributes[attribute.id]?.trim())
          if (missingAttributes.length > 0) {
            throw new Error(`Attributs manquants : ${missingAttributes.map((attribute) => attribute.name).join(", ")}`)
          }

          const severity = Number(response.severity)
          const frequency = Number(response.frequency)
          const generatedFamily = riskFamilies.find(
            (family) =>
              family.id === response.familyId ||
              family.id === response.family?.id ||
              family.name.toLowerCase() === String(response.familyName ?? response.family?.name ?? "").toLowerCase()
          )

          //La famille du contexte n'est qu'une suggestion : si la description ne lui correspond pas
          //(ex. un risque physique decrit depuis une famille RPS), l'assistant peut en proposer une autre.
          //Le niveau de prevention par defaut suit la famille retenue, les barèmes RPS et physiques differant.
          const retainedFamily =
            generatedFamily ?? riskFamilies.find((family) => family.id === getContextFamilyId()) ?? null

          openEditStep(
            createDraftWithContext({
              attributes,
              ...(Number.isFinite(severity) ? { severity } : {}),
              ...(Number.isFinite(frequency) ? { frequency } : {}),
              RiskFamilyId: retainedFamily?.id ?? null,
              prevention: getLowestPreventionValue(accountRisksSettings)
            })
          )
          stopAssistant()
        } catch {
          toast("Impossible de pré-remplir le risque avec l'assistant.", { type: "error" })
          resetToSelect()
        }
      },
      () => {
        toast("L'assistant n'a pas répondu.", { type: "error" })
        resetToSelect()
      },
      () => {}
    )
  }

  function openDescriptionMode() {
    if (!linkedSites.length && !linkedPopulations.length) {
      setDescriptionPlaceholder(DEFAULT_DESCRIPTION_PLACEHOLDER)
      setDescriptionStartMode("DESCRIBE")
      return
    }

    startAssistant("Veuillez patienter")
    SocketEmit(
      SocketCreate(),
      "task-custom",
      {
        ...ASSISTANT_INSTANT,
        preprompt: `Retourner uniquement un objet JSON valide au format {"placeholder":"Incendie cuisine"}. Le placeholder doit contenir 2 à 3 mots maximum et être concret.`,
        prompt: `Génère un objet JSON avec une clé "placeholder" contenant un exemple court et concret de situation à risque.

Contexte :
- Lieux sélectionnés : ${linkedSites.length ? linkedSites.map((site) => site.name).join(", ") : "aucun"}
- Fonctions sélectionnées : ${
          linkedPopulations.length ? linkedPopulations.map((population) => population.name).join(", ") : "aucune"
        }

La valeur de "placeholder" doit être un groupe nominal de 2 à 3 mots, par exemple "Incendie cuisine", "Chute escalier", "Agression usager".
Ne réponds jamais uniquement "Risque", "Danger", "Incident" ou un autre mot générique.
Réponds uniquement avec du JSON valide, sans markdown.`,
        sendMode: "all"
      },
      "fr",
      20,
      (res) => {
        try {
          const parsedResponse = JSON.parse(cleanJson(res)) as { placeholder?: unknown }
          const placeholder = typeof parsedResponse.placeholder === "string" ? parsedResponse.placeholder.trim() : ""
          const normalizedPlaceholder = placeholder.replace(/^Exemples?\s*:\s*/i, "").trim()
          setDescriptionPlaceholder(
            normalizedPlaceholder ? `Exemple : ${normalizedPlaceholder}` : DEFAULT_DESCRIPTION_PLACEHOLDER
          )
        } catch {
          setDescriptionPlaceholder(DEFAULT_DESCRIPTION_PLACEHOLDER)
        }
        setDescriptionStartMode("DESCRIBE")
        stopAssistant()
      },
      () => {
        setDescriptionPlaceholder(DEFAULT_DESCRIPTION_PLACEHOLDER)
        setDescriptionStartMode("DESCRIBE")
        stopAssistant()
      },
      () => {}
    )
  }

  return {
    assistantLoadingText,
    descriptionPlaceholder,
    descriptionStartMode,
    handleGenerateFromDescription,
    handleUploadImage,
    isAssistantLoading,
    openDescriptionMode,
    setDescriptionStartMode
  }
}
